import React from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, IconButton, TablePagination, CircularProgress, Box,
  Typography, Chip, useTheme
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const StatusChip = styled(Chip)(({ theme, active }) => ({
  fontWeight: 600,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(0.5),
  color:"blue",
  '&:hover': {
    backgroundColor: "white",
    color: "blue"
  },
}));

const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(0.5),
   color:"red",
  '&:hover': {
    backgroundColor:"white",
    color:"#FF4500",
  },
}));

const RedirectionTable = ({ data, loading, page, totalItems, onEdit, onDelete, onPageChange }) => {
  const theme = useTheme();

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: theme.palette.primary.main,
              '& th': {
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                fontSize: '0.875rem'
              }
            }}>
              <TableCell>Sr.No</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Match Type</TableCell>
              <TableCell>Redirect Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Activation</TableCell>
              <TableCell>Deactivation</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, i) => (
              <StyledTableRow key={row.id}>
                <TableCell>{i + 1 + (page * 10)}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{row.source}</TableCell>
                <TableCell sx={{ color: theme.palette.info.dark }}>{row.destination}</TableCell>
                <TableCell>
                  <Chip 
                    label={row.match_type} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={row.redirect_type} 
                    size="small" 
                    color="primary" 
                  />
                </TableCell>
                <TableCell>
                  <StatusChip 
                    active={row.status} 
                    label={row.status ? 'Active' : 'Inactive'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>
                  {row.activation_date ? (
                    <Typography variant="body2" color="text.secondary">
                      {new Date(row.activation_date).toLocaleDateString()}
                    </Typography>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {row.deactivation_date ? (
                    <Typography variant="body2" color="text.secondary">
                      {new Date(row.deactivation_date).toLocaleDateString()}
                    </Typography>
                  ) : '-'}
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <ActionIconButton 
                      size="small" 
                      onClick={() => onEdit(row)}
                      aria-label="edit"
                    >
                      <Edit fontSize="small" />
                    </ActionIconButton>
                    <DeleteIconButton 
                      size="small" 
                      onClick={() => onDelete(row.id)}
                      aria-label="delete"
                    >
                      <Delete fontSize="small" />
                    </DeleteIconButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
            {data.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No redirection rules found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={(e, newPage) => onPageChange(newPage)}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          '& .MuiTablePagination-toolbar': {
            padding: theme.spacing(1, 2),
          }
        }}
      />
    </Paper>
  );
};

export default RedirectionTable;